import React from 'react';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import {
  GridActionsCellItemProps,
  GridActionsColDef,
  GridColDef,
  GridRenderCellParams,
  GridRowParams,
  GridValidRowModel,
} from '@mui/x-data-grid-pro';
import { format, formatDistanceToNow, isValid } from 'date-fns';
import { Link, To } from 'react-router-dom';
import { snackNotifications, copyToClipboardWithFallback } from '../common';

export const EMPTY_VALUE = '';

function generateColumnDef(
  args: GridColDef,
  base: Partial<GridColDef>
): GridColDef {
  return {
    ...args,
    ...base,
    flex: 1,
  };
}

function generateActionsColumnDef(
  args: any,
  base: Partial<GridActionsColDef>
): GridActionsColDef {
  return {
    ...args,
    ...base,
    flex: 1,
  };
}

function getTextToRender<T>(value?: T): string {
  if (typeof value !== 'string') {
    return EMPTY_VALUE;
  }

  const trimmed = value?.trim();
  return trimmed || EMPTY_VALUE;
}

/**
 * Generate text column
 * @param args base column definition
 */
export function textColumn(args: GridColDef, testid?: string): GridColDef {
  return generateColumnDef(args, {
    renderCell: (params: GridRenderCellParams) => (
      <div
        style={{
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          width: '250px',
          textOverflow: 'ellipsis',
        }}
        title={params?.formattedValue}
        data-testid={testid}
      >
        <Box
          component="span"
          sx={(theme) => ({
            color:
              theme.palette.mode === 'dark'
                ? theme.palette.text.primary
                : 'rgba(0, 0, 0, 0.8)',
          })}
        >
          {getTextToRender(params?.formattedValue)}
        </Box>
      </div>
    ),
  });
}

export function htmlColumn(args: GridColDef, testid?: string): GridColDef {
  return generateColumnDef(args, {
    renderCell: args.renderCell,
  });
}

/**
 * Generate date distance column
 * @example  "2 minutes ago", "13 days ago"
 * @param args base column definition
 */
export function dateDistanceColumn(args: GridColDef): GridColDef {
  return textColumn({
    ...args,
    valueFormatter: (value: unknown) =>
      value instanceof Date && isValid(value)
        ? formatDistanceToNow(value, { addSuffix: true })
        : EMPTY_VALUE,
  });
}

/**
 * Generate date column
 * @example  "8/31/2022, 2:22 PM"
 * @param args base column definition
 * @param formatString custom format string, defaults to 'Pp '
 */
export function dateColumn(
  args: GridColDef,
  formatString = 'Pp ',
  testId?: string
): GridColDef {
  return textColumn(
    {
      ...args,
      valueFormatter: (value: unknown) =>
        value instanceof Date && isValid(value)
          ? format(value, formatString)
          : EMPTY_VALUE,
    },
    testId
  );
}

const LinkText = styled('span')(({ theme }) => ({
  fontWeight: 400,
  // Light stays byte-identical to the pre-theme original; only dark routes to
  // tokens so it inverts. (In light, text.primary/secondary do NOT equal the
  // original literals, so they are pinned rather than tokenized.)
  color:
    theme.palette.mode === 'dark'
      ? theme.palette.text.primary
      : 'rgba(0, 0, 0, 0.8)',
  '&:hover': {
    color:
      theme.palette.mode === 'dark' ? theme.palette.text.secondary : 'darkgrey',
  },
}));

/**
 * Generate link column
 * @param args base column definition
 */
export function linkColumn<R extends GridValidRowModel, V>(
  args: GridColDef,
  to: (rowValue?: R, data?: V) => To
): GridColDef {
  return generateColumnDef(args, {
    renderCell: (params: GridRenderCellParams<R, V>) => (
      <Link
        to={to(params.row, params.value)}
        style={{ textDecoration: 'none' }}
      >
        <LinkText>
          {getTextToRender(params?.formattedValue as unknown)}
        </LinkText>
      </Link>
    ),
  });
}

const BoldLinkStyle = styled('span')(({ theme }) => ({
  fontWeight: 700,
  // Light byte-identical to the original; dark routes to tokens (see LinkText).
  color: theme.palette.mode === 'dark' ? theme.palette.text.primary : '#000000',
  '&:hover': {
    color:
      theme.palette.mode === 'dark' ? theme.palette.text.secondary : '#4e4e4e',
  },
}));

/**
 * Generate bold column
 * @param args base column definition
 */
export function boldLinkColumn<R extends GridValidRowModel, V>(
  args: GridColDef & { target?: string },
  to: (rowValue?: R, data?: V) => To
): GridColDef {
  return generateColumnDef(args, {
    renderCell: (params: GridRenderCellParams<R, V>) => (
      <Link
        to={to(params.row, params.value)}
        target={args.target}
        style={{ textDecoration: 'none' }}
      >
        <BoldLinkStyle>
          {getTextToRender(params?.formattedValue as unknown)}
        </BoldLinkStyle>
      </Link>
    ),
  });
}

/**
 * Generate bold column
 * @param args base column definition
 */
export function boldColumn(args: GridColDef): GridColDef {
  return generateColumnDef(args, {
    renderCell: (params: GridRenderCellParams) => (
      <span style={{ fontWeight: '600' }}>
        {getTextToRender(params?.formattedValue)}
      </span>
    ),
  });
}

/**
 * Generate "IsActive" {@link Chip} column
 * @param args base column definition
 */
export function activeColumn(args: GridColDef): GridColDef {
  return generateColumnDef(args, {
    renderCell: (params) => (
      <Chip
        label={params?.value ? 'Yes' : 'No'}
        style={{
          minWidth: '55px',
          padding: '3px 4px',
          fontSize: '12px',
          color: '#FFFFFF',
          backgroundColor: params?.value ? '#4CAF50' : '#F44336',
        }}
      />
    ),
  });
}

/**
 * clickToCopy column allows user to click on the text to copy it to clipboard
 * @param args base column definition
 * @param testid test id for the column
 */
export function clickToCopy(args: GridColDef, testid?: string): GridColDef {
  return generateColumnDef(args, {
    renderCell: (params: GridRenderCellParams) => (
      <div
        style={{
          cursor: 'pointer',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          width: '250px',
          textOverflow: 'ellipsis',
        }}
        title="Click to copy"
        data-testid={testid}
      >
        <Box
          component="span"
          sx={(theme) => ({
            color:
              theme.palette.mode === 'dark'
                ? theme.palette.text.primary
                : 'rgba(0, 0, 0, 0.8)',
            cursor: 'pointer',
            fontWeight: 'bold',
          })}
          onClick={async () => {
            try {
              await copyToClipboardWithFallback(params.value);
              snackNotifications.info('Copied to clipboard: ', params.value);
            } catch (err) {
              snackNotifications.error('Failed to copy to clipboard', '');
            }
          }}
        >
          {getTextToRender(params?.formattedValue)}
        </Box>
      </div>
    ),
  });
}

export function actionsColumn(
  actionItems: (
    params: GridRowParams
  ) => React.ReactElement<
    GridActionsCellItemProps,
    string | React.JSXElementConstructor<any>
  >[]
): GridActionsColDef {
  return generateActionsColumnDef(
    { field: 'actions', type: 'actions', align: 'right', paddingRight: '20px' },
    {
      getActions: (params: GridRowParams) => actionItems(params),
    }
  );
}
